import { ref } from 'vue';
import {
  importZoomRecordingApi,
} from '@/api/recordingApi';

/**
 * Composable for managing Zoom recording import flow:
 * submit → handle errors or close modal on success.
 * Transcription polling is delegated to useTranscription (same as upload recording).
 *
 * @param {Object} options
 * @param {import('vue').Ref<string>} options.patientId
 * @param {import('vue').Ref<string>} options.encounterNoteId
 * @param {Function} options.onImportStarted - Called with { sessionId } after accepted
 */
export function useImportRecording({ patientId, encounterNoteId, onImportStarted }) {
  // --- Modal state ---
  const showImportRecordingModal = ref(false);
  const isImporting = ref(false);
  const importError = ref('');
  const importErrorType = ref(''); // 'password' | 'link' | ''

  const clearImportError = () => {
    importError.value = '';
    importErrorType.value = '';
  };

  const handleImportRecording = async ({ zoomLink, passcode }) => {
    importError.value = '';
    importErrorType.value = '';
    isImporting.value = true;

    try {
      const result = await importZoomRecordingApi({
        recording_url: zoomLink,
        password: passcode || undefined,
        patient_id: patientId.value,
        encounter_id: encounterNoteId.value || undefined,
      });

      const newSessionId = result.session_id;

      // Close modal and let useTranscription handle polling with editor overlay
      isImporting.value = false;
      showImportRecordingModal.value = false;

      // Notify parent to push session and start transcription polling
      onImportStarted?.({ sessionId: newSessionId });
    } catch (error) {
      console.error('Import recording failed:', error);
      isImporting.value = false;

      // Extract error code from zoom/transcribe error response
      const errorData = error.response?.data?.error;
      const errorCode = errorData?.message?.code;

      if (errorCode) {
        const errorMessages = {
          ZOOM_PASSWORD_REQUIRED: 'This recording is password-protected. Please enter the passcode.',
          ZOOM_PASSWORD_INCORRECT: 'Incorrect passcode. Please try again.',
          ZOOM_PROCESSING_FAILED: 'Failed to process the Zoom recording. Please try a different link.',
          ZOOM_ACCESS_DENIED: 'This recording is private or has expired. Please check the link.',
        };
        importError.value = errorMessages[errorCode] || errorData.message?.message || 'Failed to import recording. Please try again.';
        importErrorType.value =
          errorCode === 'ZOOM_PASSWORD_REQUIRED' || errorCode === 'ZOOM_PASSWORD_INCORRECT' ? 'password' : 'link';
      } else {
        importError.value = 'Failed to submit recording for import. Please try again.';
        importErrorType.value = 'link';
      }
    }
  };

  return {
    // State
    showImportRecordingModal,
    isImporting,
    importError,
    importErrorType,

    // Methods
    handleImportRecording,
    clearImportError,
  };
}
